import React, { useMemo, useState } from 'react'

import SearchAndSelectPopover, {
  SearchAndSelectPopoverProps
} from 'components_new/molecules/SearchAndSelectPopover'
import SelectButtonBase, {
  SelectButtonBaseProps
} from 'components_new/molecules/SelectButtonBase'

import { getSelectedOption, getSelectedOptions } from 'utils/functions'

type SearchAndSelectButtonBaseProps = Omit<
  SelectButtonBaseProps,
  'focused' | 'value'
> & {
  options: SearchAndSelectPopoverProps['options']
  PopoverProps?: Omit<
    SearchAndSelectPopoverProps,
    | 'children'
    | 'multiple'
    | 'onBlur'
    | 'onChange'
    | 'onFocus'
    | 'onSubmit'
    | 'options'
    | 'value'
  >
}

type SearchAndSelectButtonProps =
  | (SearchAndSelectButtonBaseProps & {
      multiple?: true
      onChange?: (value: string[]) => void
      onSubmit?: (value: string[]) => void
      value: string[]
    })
  | (SearchAndSelectButtonBaseProps & {
      multiple: false
      onChange?: (value: string | null) => void
      onSubmit?: (value: string | null) => void
      value: string | null
    })

const SearchAndSelectButton = React.forwardRef<
  HTMLDivElement,
  SearchAndSelectButtonProps
>((props, ref) => {
  const {
    multiple,
    onChange,
    onSubmit,
    options,
    PopoverProps,
    value,
    ...ButtonProps
  } = props

  const [focused, setFocused] = useState<boolean>(false)

  const renderedValue = useMemo((): string | null => {
    if (Array.isArray(value)) {
      const selected = getSelectedOptions(
        value,
        options as { id: string; title: string }[]
      )

      return selected.length > 0
        ? selected.map((o) => o.title).join(', ')
        : null
    }

    return (
      getSelectedOption(value, options as { id: string; title: string }[])
        ?.title ?? null
    )
  }, [value, options])

  const searchProps =
    multiple === false
      ? ({ multiple: false, onChange, onSubmit, value } as const)
      : ({ multiple: true as const, onChange, onSubmit, value } as const)

  return (
    <>
      <SearchAndSelectPopover
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        options={options}
        {...searchProps}
        {...PopoverProps}
      >
        <SelectButtonBase
          active={renderedValue !== null}
          ref={ref}
          focused={focused}
          value={renderedValue}
          {...ButtonProps}
        />
      </SearchAndSelectPopover>
    </>
  )
})

SearchAndSelectButton.displayName = 'SearchAndSelectButton'

export default SearchAndSelectButton
